import React, { useEffect, useState, useMemo } from 'react';
import { Card, CardContent, CardHeader } from '@/Components/ui/card';
import { Heading } from '@/Components/ui/typography';
import { Text } from '@/Components/ui/typography';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Components/ui/select';
import { companyCreditsApi } from './companyCreditsApi';
import { Skeleton } from '@/Components/ui/skeleton';
import { Calendar } from 'lucide-react';

function creditColor(value) {
  if (value > 0) return 'text-green-600';
  if (value < 0) return 'text-red-600';
  return 'text-muted-foreground';
}

function getMonthKey(date) {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function getMonthLabel(key) {
  const [y, m] = key.split('-').map(Number);
  const d = new Date(y, m - 1, 1);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function isInMonth(createdAt, monthFilter) {
  if (!monthFilter || monthFilter === 'all') return true;
  if (monthFilter === 'this_month') {
    const now = new Date();
    const key = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    return getMonthKey(createdAt) === key;
  }
  if (monthFilter === 'last_month') {
    const now = new Date();
    const last = new Date(now.getFullYear(), now.getMonth() - 1);
    const key = `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, '0')}`;
    return getMonthKey(createdAt) === key;
  }
  return getMonthKey(createdAt) === monthFilter;
}

export function CreditUsageHistory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [monthFilter, setMonthFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await companyCreditsApi.getTransactions();
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const monthOptions = useMemo(() => {
    const now = new Date();
    const options = [
      { value: 'all', label: 'All time' },
      { value: 'this_month', label: 'This month' },
      { value: 'last_month', label: 'Last month' },
    ];
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      options.push({ value: key, label: getMonthLabel(key) });
    }
    return options;
  }, []);

  const filteredRows = useMemo(() => {
    return rows.filter((r) => r.created_at && isInMonth(r.created_at, monthFilter));
  }, [rows, monthFilter]);

  const groupedByMonth = useMemo(() => {
    const map = {};
    filteredRows.forEach((r) => {
      const key = getMonthKey(r.created_at);
      if (!map[key]) map[key] = [];
      map[key].push(r);
    });
    return Object.keys(map)
      .sort((a, b) => b.localeCompare(a))
      .map((key) => ({ key, label: getMonthLabel(key), transactions: map[key] }));
  }, [filteredRows]);

  const periodUsed = useMemo(() => {
    return filteredRows.reduce((sum, r) => {
      const c = Number(r.credits);
      return c < 0 ? sum + Math.abs(c) : sum;
    }, 0);
  }, [filteredRows]);

  const periodLabel = useMemo(() => {
    if (monthFilter === 'all') return 'All time';
    if (monthFilter === 'this_month') return 'This month';
    if (monthFilter === 'last_month') return 'Last month';
    return getMonthLabel(monthFilter);
  }, [monthFilter]);

  return (
    <Card className="w-full bg-white border border-border rounded-xl overflow-hidden shadow-sm">
      <CardHeader className="p-4 md:p-6 border-b border-border bg-muted/30">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <Heading level="h2" className="text-xl font-semibold text-foreground m-0">
              Credit usage
            </Heading>
            <Text size="sm" className="text-muted-foreground mt-1 m-0">
              View usage by month and date
            </Text>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <Select value={monthFilter} onValueChange={setMonthFilter}>
              <SelectTrigger className="w-[180px] bg-background">
                <SelectValue placeholder="Select period" />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {!loading && !error && filteredRows.length > 0 && (
          <div className="mt-2 pt-2 border-t border-border/50">
            {/* <Text size="sm" className="text-muted-foreground m-0">
              Credits used in <strong className="text-foreground">{periodLabel}</strong>:{' '}
              <strong className="text-foreground">{periodUsed}</strong>
            </Text> */}
          </div>
        )}
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="p-6 space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : error ? (
          <div className="p-6 text-muted-foreground text-sm">
            Unable to load usage history. Your backend may not expose this yet.
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="p-6 text-muted-foreground text-sm">
            {rows.length === 0
              ? 'No transactions yet.'
              : `No transactions in ${periodLabel.toLowerCase()}.`}
          </div>
        ) : monthFilter === 'all' ? (
          <div className="overflow-x-auto max-h-[360px] overflow-y-auto">
            {groupedByMonth.map(({ key, label, transactions }) => (
              <div key={key} className="border-b border-border last:border-b-0">
                <div className="bg-muted/40 px-4 py-2 text-sm font-semibold text-foreground sticky top-0">
                  {label}
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-muted-foreground uppercase text-xs tracking-wider">
                    <tr>
                      <th className="p-4 text-left font-semibold">Date</th>
                      <th className="p-4 text-left font-semibold">Action</th>
                      <th className="p-4 text-left font-semibold">Credits</th>
                      <th className="p-4 text-left font-semibold">Balance after</th>
                      <th className="p-4 text-left font-semibold">Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions
                      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                      .map((r) => (
                        <tr key={r.id} className="border-t border-border hover:bg-muted/30 transition">
                          <td className="p-4 text-muted-foreground">
                            {r.created_at ? new Date(r.created_at).toLocaleString() : '–'}
                          </td>
                          <td className="p-4 font-medium text-foreground capitalize">
                            {(r.action_type || '').replace(/_/g, ' ')}
                          </td>
                          <td className={`p-4 font-semibold ${creditColor(r.credits)}`}>
                            {r.credits > 0 ? `+${r.credits}` : r.credits}
                          </td>
                          <td className="p-4 text-foreground font-medium">
                            {r.balance_after ?? '–'}
                          </td>
                          <td className="p-4 text-muted-foreground">
                            {r.reference_type || r.description || '–'}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[360px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground uppercase text-xs tracking-wider">
                <tr>
                  <th className="p-4 text-left font-semibold">Date</th>
                  <th className="p-4 text-left font-semibold">Action</th>
                  <th className="p-4 text-left font-semibold">Credits</th>
                  <th className="p-4 text-left font-semibold">Balance after</th>
                  <th className="p-4 text-left font-semibold">Reference</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows
                  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                  .map((r) => (
                    <tr key={r.id} className="border-t border-border hover:bg-muted/30 transition">
                      <td className="p-4 text-muted-foreground">
                        {r.created_at ? new Date(r.created_at).toLocaleString() : '–'}
                      </td>
                      <td className="p-4 font-medium text-foreground capitalize">
                        {(r.action_type || '').replace(/_/g, ' ')}
                      </td>
                      <td className={`p-4 font-semibold ${creditColor(r.credits)}`}>
                        {r.credits > 0 ? `+${r.credits}` : r.credits}
                      </td>
                      <td className="p-4 text-foreground font-medium">
                        {r.balance_after ?? '–'}
                      </td>
                      <td className="p-4 text-muted-foreground">
                        {r.reference_type || r.description || '–'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
